import React, {useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useAuth, ProtectedRoute} from '@betstarters/auth';
import i18n from './i18n';
import {
    AppShell,
    BackofficeHeader,
    BackofficeSidebar,
    LoginPage,
    ProfilePage,
    VersionFooter,
} from '@betstarters/ui';
import {
    BarChart3, TrendingUp, Wallet, Users, Gamepad2,
    Database, Bell, Settings, BookOpen, FileJson, Calculator, Coins, Gift, Building2, Trophy,
} from 'lucide-react';
import {TimezoneSelector} from './components/TimezoneSelector';
import api from './api/client';
import {clearSiteCache, SiteProvider} from './components/SiteFilter';
import {PerformanceDashboard} from './pages/PerformanceDashboard';
import {GGRReport} from './pages/GGRReport';
import {FinancialReport} from './pages/FinancialReport';
import {ActivePlayers} from './pages/ActivePlayers';
import {CohortRetention} from './pages/CohortRetention';
import {GamePerformance} from './pages/GamePerformance';
import {QueryBuilder} from './pages/QueryBuilder';
import {Settings as SettingsPage} from './pages/Settings';
import {AlertManager} from './pages/AlertManager';
import {DataDictionary} from './pages/DataDictionary';
import {PamIntegration} from './pages/PamIntegration';
import {KPIGlossary} from './pages/KPIGlossary';
import {Currencies} from './pages/Currencies';
import {PlayerDetail} from './pages/PlayerDetail';
import {BonusReport} from './pages/BonusReport';
import {SiteComparison} from './pages/SiteComparison';
import {SportsbookReport} from './pages/SportsbookReport';
import {DataPilotProvider} from './components/datapilot/DataPilotProvider';
import {DataPilot} from './components/datapilot/DataPilotChat';

function LoadingSpinner() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-background">
            <div className="animate-spin rounded-full border-4 border-muted border-t-accent h-12 w-12"/>
        </div>
    );
}

function ReportingLayout({children}: { children: React.ReactNode }) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user, logout} = useAuth();

    // Honor the user's preferred language once it is known.
    useEffect(() => {
        const lang = (user as { pref_language?: string; language?: string } | null)?.pref_language
            || (user as { pref_language?: string; language?: string } | null)?.language;
        if (lang && ['en', 'it', 'es', 'pt'].includes(lang) && lang !== i18n.language) {
            i18n.changeLanguage(lang);
        }
    }, [user]);

    const handleLogout = () => {
        logout();
        clearSiteCache();
        navigate('/login');
    };

    const sections = [
        {
            label: t('sidebar.sectionDashboard'), items: [
                {to: '/dashboard/performance', icon: BarChart3, label: t('sidebar.overview')},
            ]
        },
        {
            label: t('sidebar.sectionReports'), items: [
                {to: '/reports/ggr', icon: TrendingUp, label: t('sidebar.ggrNgr')},
                {to: '/reports/financial', icon: Wallet, label: t('sidebar.financial')},
                {to: '/reports/players', icon: Users, label: t('sidebar.activePlayers')},
                {to: '/reports/cohorts', icon: Users, label: t('sidebar.cohortRetention')},
                {to: '/reports/games', icon: Gamepad2, label: t('sidebar.gamesBrands')},
                {to: '/reports/sportsbook', icon: Trophy, label: t('sidebar.sportsbook', 'Sportsbook')},
                {to: '/reports/bonus', icon: Gift, label: t('sidebar.bonus', 'Bonus')},
                {to: '/reports/sites', icon: Building2, label: t('sidebar.siteComparison', 'Site Comparison')},
            ]
        },
        {
            label: t('sidebar.sectionTools'), items: [
                {to: '/tools/query', icon: Database, label: t('sidebar.queryBuilder')},
                {to: '/tools/currencies', icon: Coins, label: t('sidebar.currencies', 'Valute')},
                {to: '/dashboard/alerts', icon: Bell, label: t('sidebar.alerts')},
                {to: '/dashboard/settings', icon: Settings, label: t('sidebar.settings')},
            ]
        },
        {
            label: t('sidebar.sectionDocumentation'), items: [
                {to: '/docs/data-dictionary', icon: BookOpen, label: t('sidebar.dataDictionary')},
                {to: '/docs/pam-integration', icon: FileJson, label: t('sidebar.pamIntegration')},
                {to: '/docs/kpi-glossary', icon: Calculator, label: t('sidebar.kpiGlossary')},
            ]
        },
    ];

    const sidebar = (
        <BackofficeSidebar
            subtitle="Reporting & Analytics"
            sections={sections}
            responsive
        />
    );

    const header = (
        <BackofficeHeader
            userEmail={user?.email}
            userRole={(user as { platform_role?: string } | null)?.platform_role}
            onLogout={handleLogout}
            onProfileClick={() => navigate('/dashboard/profile')}
            showLanguageSwitcher
            extra={<TimezoneSelector/>}
        />
    );

    return (
        <DataPilotProvider>
            <AppShell sidebar={sidebar} header={header} footer={<VersionFooter />}>
                {children}
            </AppShell>
            <DataPilot/>
        </DataPilotProvider>
    );
}

const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({children}) => (
    <ProtectedRoute
        loadingFallback={<LoadingSpinner/>}
        fallback={<Navigate to="/login" replace/>}
    >
        <SiteProvider>
            <ReportingLayout>{children}</ReportingLayout>
        </SiteProvider>
    </ProtectedRoute>
);

function ReportingLoginPage() {
    const navigate = useNavigate();
    const {refreshUser} = useAuth();
    return (
        <LoginPage
            subtitle="Reporting & Analytics"
            api={api}
            onSuccess={async () => {
                // The kit LoginPage stores tokens via @betstarters/api.setTokens,
                // but the AuthProvider context needs a manual refresh so
                // ProtectedRoute sees the new user before we navigate.
                await refreshUser();
                navigate('/dashboard/performance');
            }}
        />
    );
}

function ReportingProfilePage() {
    const {user, refreshUser} = useAuth();
    if (!user) return null;
    return <ProfilePage user={user} api={api} onProfileUpdated={refreshUser}/>;
}

export default function App() {
    return (
        <Router>
            <Routes>
                {/* Public */}
                <Route path="/login" element={<ReportingLoginPage/>}/>

                {/* Dashboard */}
                <Route path="/dashboard/performance" element={<ProtectedPage><PerformanceDashboard/></ProtectedPage>}/>
                <Route path="/dashboard/alerts" element={<ProtectedPage><AlertManager/></ProtectedPage>}/>
                <Route path="/dashboard/settings" element={<ProtectedPage><SettingsPage/></ProtectedPage>}/>
                <Route path="/dashboard/profile" element={<ProtectedPage><ReportingProfilePage/></ProtectedPage>}/>

                {/* Reports */}
                <Route path="/reports/ggr" element={<ProtectedPage><GGRReport/></ProtectedPage>}/>
                <Route path="/reports/financial" element={<ProtectedPage><FinancialReport/></ProtectedPage>}/>
                <Route path="/reports/players" element={<ProtectedPage><ActivePlayers/></ProtectedPage>}/>
                <Route path="/reports/players/detail" element={<ProtectedPage><PlayerDetail/></ProtectedPage>}/>
                <Route path="/reports/cohorts" element={<ProtectedPage><CohortRetention/></ProtectedPage>}/>
                <Route path="/reports/games" element={<ProtectedPage><GamePerformance/></ProtectedPage>}/>
                <Route path="/reports/sportsbook" element={<ProtectedPage><SportsbookReport/></ProtectedPage>}/>
                <Route path="/reports/bonus" element={<ProtectedPage><BonusReport/></ProtectedPage>}/>
                <Route path="/reports/sites" element={<ProtectedPage><SiteComparison/></ProtectedPage>}/>

                {/* Tools */}
                <Route path="/tools/query" element={<ProtectedPage><QueryBuilder/></ProtectedPage>}/>
                <Route path="/tools/currencies" element={<ProtectedPage><Currencies/></ProtectedPage>}/>

                {/* Documentation */}
                <Route path="/docs/data-dictionary" element={<ProtectedPage><DataDictionary/></ProtectedPage>}/>
                <Route path="/docs/pam-integration" element={<ProtectedPage><PamIntegration/></ProtectedPage>}/>
                <Route path="/docs/kpi-glossary" element={<ProtectedPage><KPIGlossary/></ProtectedPage>}/>

                {/* Catch All */}
                <Route path="/" element={<Navigate to="/dashboard/performance" replace/>}/>
                <Route path="*" element={<Navigate to="/dashboard/performance" replace/>}/>
            </Routes>
        </Router>
    );
}
